import { useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ChevronDown, Grip, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils/cn';
import { useAuthStore } from '@/lib/auth/store';
import { usePermissionStore } from '@/lib/permissions/store';
import { useCurrentUserModules } from '@/lib/permissions/use-current-modules';
import { useThemeStore } from '@/lib/theme/store';
import { filterNav } from './filter-nav';
import { NAV_ITEMS, isGroup, type NavGroup, type NavLeaf } from './nav-config';
import { isRouteActive } from './route-active';
import { SidebarContent } from './SidebarContent';

interface SidebarProps {
  brand: { logoUrl?: string | null; company?: string | null };
  showDragHandle?: boolean;
  dragHandle?: React.ReactNode;
}

/**
 * Vertical sidebar (used for Left or Right placement).
 */
export function Sidebar({ brand, showDragHandle, dragHandle }: SidebarProps) {
  const placement = useThemeStore((s) => s.sidebarPlacement);
  const collapsed = useThemeStore((s) => s.sidebarCollapsed);
  const toggle = useThemeStore((s) => s.toggleSidebar);

  const isLeft = placement === 'left';
  const isRight = placement === 'right';
  const width = collapsed ? 'w-16' : 'w-64';

  return (
    <aside
      className={cn(
        'hidden h-screen shrink-0 flex-col bg-sidebar text-sidebar-foreground lg:flex',
        width,
        // Flat outer edge — the carve-out scoop only reads as a smooth
        // curve when the sidebar has a straight right edge for the
        // active pill to break through. A subtle shadow keeps the
        // sidebar visually separated from the page.
        isLeft && 'shadow-[2px_0_12px_rgba(0,0,0,0.04)]',
        isRight && 'shadow-[-2px_0_12px_rgba(0,0,0,0.04)]',
      )}
    >
      <div
        className={cn(
          'flex h-14 items-center gap-2 border-b border-sidebar-border px-3',
          collapsed && 'justify-center px-0',
        )}
      >
        {!collapsed && (
          <>
            {brand.logoUrl ? (
              <img src={brand.logoUrl} alt={brand.company ?? 'Logo'} className="h-7 w-auto" />
            ) : (
              <div className="flex h-7 w-7 items-center justify-center rounded bg-sidebar-accent text-xs font-bold uppercase tracking-tight text-sidebar-foreground">
                {(brand.company ?? 'OB').slice(0, 2)}
              </div>
            )}
            <span className="truncate text-sm font-semibold tracking-tight">
              {brand.company ?? 'OBD3'}
            </span>
          </>
        )}
        {collapsed && (
          <div className="flex h-7 w-7 items-center justify-center rounded bg-sidebar-accent text-xs font-bold uppercase tracking-tight text-sidebar-foreground">
            OB
          </div>
        )}
      </div>

      {/* pr-0 so the active pill can hug the sidebar's right edge,
          matching the design 1 carve-out where the pill visually connects
          to the page. */}
      <nav className="flex-1 overflow-y-auto pl-2 pr-0 py-3">
        <SidebarContent compact={collapsed} />
      </nav>

      <div className="flex items-center justify-between gap-1 border-t border-sidebar-border p-2">
        {showDragHandle && (
          <span className="flex-1">
            {dragHandle ?? (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-foreground"
                aria-label="Drag to dock"
              >
                <Grip className="h-4 w-4" />
              </Button>
            )}
          </span>
        )}
        <Button
          variant="ghost"
          size="icon"
          onClick={toggle}
          className="h-8 w-8 text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-foreground"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
        </Button>
      </div>
    </aside>
  );
}

/* ------------------------------------------------------------------ */
/* Top placement: two-row nav.                                         */
/*   Row 1 (always)     : brand + horizontally-scrollable items.       */
/*   Row 2 (contextual) : children of either the hovered group OR the  */
/*                        group containing the active route. Collapses */
/*                        to height 0 when neither.                    */
/* ------------------------------------------------------------------ */

export function TopBar({
  brand,
  dragHandle,
}: {
  brand: { logoUrl?: string | null; company?: string | null };
  dragHandle?: React.ReactNode;
}) {
  const role = useAuthStore((s) => s.role);
  const isSubSuperAdmin = useAuthStore((s) => s.isSubSuperAdmin());
  const perms = usePermissionStore((s) => s.perms);
  const { modules } = useCurrentUserModules();
  const location = useLocation();

  const items = useMemo(
    () => filterNav(NAV_ITEMS, { role, isSubSuperAdmin, perms, modules }),
    [role, isSubSuperAdmin, perms, modules],
  );

  const [hoveredId, setHoveredId] = useState<string | null>(null);

  const activeGroup = useMemo<NavGroup | null>(() => {
    const found = items.find(
      (item) => isGroup(item) && item.children.some((c) => isRouteActive(location.pathname, c.to)),
    );
    return found && isGroup(found) ? found : null;
  }, [items, location.pathname]);

  const hoveredGroup = useMemo<NavGroup | null>(() => {
    if (!hoveredId) return null;
    const found = items.find((item) => isGroup(item) && item.label === hoveredId);
    return found && isGroup(found) ? found : null;
  }, [items, hoveredId]);

  const visibleGroup = hoveredGroup ?? activeGroup;

  return (
    <div
      className="sticky top-14 z-20 hidden border-b border-sidebar-border bg-sidebar text-sidebar-foreground lg:block"
      onMouseLeave={() => setHoveredId(null)}
    >
      {/* Primary row */}
      <div className="flex h-12 items-center gap-3 px-4">
        <div className="flex shrink-0 items-center gap-2">
          {brand.logoUrl ? (
            <img src={brand.logoUrl} alt={brand.company ?? 'Logo'} className="h-6 w-auto" />
          ) : (
            <div className="flex h-6 w-6 items-center justify-center rounded bg-sidebar-accent text-2xs font-bold uppercase tracking-tight text-sidebar-foreground">
              {(brand.company ?? 'OB').slice(0, 2)}
            </div>
          )}
          <span className="hidden truncate text-sm font-semibold tracking-tight md:inline">
            {brand.company ?? 'OBD3'}
          </span>
        </div>
        <div className="h-5 w-px shrink-0 bg-sidebar-border" />
        <nav className="relative min-w-0 flex-1">
          <div className="overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <ul className="flex flex-nowrap items-center gap-0.5">
              {items.map((item, idx) =>
                isGroup(item) ? (
                  <TopGroupButton
                    key={`${item.label}-${idx}`}
                    group={item}
                    isHovered={hoveredId === item.label}
                    containsActive={item.children.some((c) =>
                      isRouteActive(location.pathname, c.to),
                    )}
                    onHover={() => setHoveredId(item.label)}
                  />
                ) : (
                  <TopLeafLink key={`${item.label}-${idx}`} leaf={item} />
                ),
              )}
            </ul>
          </div>
          <span
            aria-hidden
            className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-sidebar to-transparent"
          />
        </nav>
        {dragHandle && <div className="shrink-0">{dragHandle}</div>}
      </div>

      {/* Contextual sub-bar */}
      <div
        className={cn(
          'grid transition-[grid-template-rows] duration-200 ease-out',
          visibleGroup ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
        )}
      >
        <div className="overflow-hidden">
          {visibleGroup && (
            <div className="flex h-10 items-center gap-1.5 overflow-x-auto border-t border-sidebar-border/50 bg-sidebar-accent/20 px-4">
              <span className="mr-2 shrink-0 text-2xs font-semibold uppercase tracking-wider text-sidebar-foreground/50">
                {visibleGroup.label}
              </span>
              {visibleGroup.children.map((child) => (
                <TopSubLink key={child.to} leaf={child} pathname={location.pathname} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function TopLeafLink({ leaf }: { leaf: NavLeaf }) {
  return (
    <li className="shrink-0">
      <NavLink
        to={leaf.to}
        end={leaf.to === '/'}
        className={({ isActive }) =>
          cn(
            'relative inline-flex items-center gap-1.5 whitespace-nowrap px-3 py-2 text-sm font-medium transition-colors',
            'text-sidebar-foreground/75 hover:text-sidebar-foreground',
            isActive && 'text-sidebar-foreground',
            'after:absolute after:inset-x-2 after:bottom-0 after:h-[2px] after:rounded-full after:bg-primary after:transition-opacity',
            isActive ? 'after:opacity-100' : 'after:opacity-0 hover:after:opacity-40',
          )
        }
      >
        {leaf.label}
      </NavLink>
    </li>
  );
}

function TopGroupButton({
  group,
  isHovered,
  containsActive,
  onHover,
}: {
  group: NavGroup;
  isHovered: boolean;
  containsActive: boolean;
  onHover: () => void;
}) {
  return (
    <li className="shrink-0" onMouseEnter={onHover}>
      <button
        type="button"
        className={cn(
          'relative inline-flex items-center gap-1 whitespace-nowrap px-3 py-2 text-sm font-medium transition-colors',
          'text-sidebar-foreground/75 hover:text-sidebar-foreground',
          (isHovered || containsActive) && 'text-sidebar-foreground',
          'after:absolute after:inset-x-2 after:bottom-0 after:h-[2px] after:rounded-full after:bg-primary after:transition-opacity',
          containsActive
            ? 'after:opacity-100'
            : isHovered
              ? 'after:opacity-40'
              : 'after:opacity-0',
        )}
      >
        {group.label}
        <ChevronDown
          className={cn('h-3 w-3 transition-transform', isHovered && 'rotate-180')}
        />
      </button>
    </li>
  );
}

function TopSubLink({ leaf, pathname }: { leaf: NavLeaf; pathname: string }) {
  const active = isRouteActive(pathname, leaf.to);
  return (
    <NavLink
      to={leaf.to}
      className={cn(
        'inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-md px-3 py-1.5 text-sm transition-colors',
        active
          ? 'bg-primary/15 font-medium text-sidebar-foreground'
          : 'text-sidebar-foreground/70 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground',
      )}
    >
      <span
        className={cn(
          'h-1.5 w-1.5 rounded-full transition-colors',
          active ? 'bg-primary' : 'bg-sidebar-muted/40',
        )}
      />
      {leaf.label}
    </NavLink>
  );
}
